"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SearchableCombobox, type FormOption } from '@/forms/shared/components/searchable-combobox';
import { CamundaUserService } from '@/lib/camunda/user-service';
import type { CamundaUser } from '@/lib/camunda/types';
import { useFormModeGuard } from './use-form-mode-guard';
import { z } from 'zod';

const DEFAULT_GROUP = 'camunda-admin';
const SEARCH_DEBOUNCE_MS = 300;
const labelValueSchema = z.string().min(1);

const mapUserToOption = (user: CamundaUser): FormOption => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  const label = fullName || user.id;
  const email = user.email?.trim();

  return {
    value: user.id,
    label,
    displayLabel: email ? `${label} (${email})` : label,
  };
};

interface CamundaUserSelectorFieldProps {
  name: string;
  label: string;
  labelFieldName?: string;
  required?: boolean;
  disabled?: boolean;
  groupId?: string;
  userScope?: 'group' | 'all';
}

export function CamundaUserSelectorField(props: CamundaUserSelectorFieldProps) {
  const scopeKey = props.userScope === 'all' ? 'all' : (props.groupId ?? DEFAULT_GROUP);

  return <CamundaUserSelectorFieldContent key={scopeKey} {...props} />;
}

function CamundaUserSelectorFieldContent({
  name,
  label,
  labelFieldName,
  required = false,
  disabled = false,
  groupId = DEFAULT_GROUP,
  userScope = 'group',
}: CamundaUserSelectorFieldProps) {
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const { watch, setValue } = useFormContext();
  const fieldValue = watch(name);
  const resolvedLabelFieldName = labelFieldName ?? `${name}_label`;
  const labelValue = watch(resolvedLabelFieldName);

  const isAllScope = userScope === 'all';
  const [users, setUsers] = useState<CamundaUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupInitialized, setGroupInitialized] = useState(false);
  const [allScopeActive, setAllScopeActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchRequestId = useRef(0);

  useEffect(() => {
    if (!sideEffectsAllowed || isAllScope) return;
    if (groupInitialized) return;

    let cancelled = false;

    const loadUsers = async () => {
      setLoading(true);
      try {
        const groupUsers = await CamundaUserService.listUsersByGroup(groupId);
        if (!cancelled) {
          setUsers(groupUsers);
          setGroupInitialized(true);
        }
      } catch (error) {
        console.error('Error loading Camunda users:', error);
        if (!cancelled) {
          setUsers([]);
          setGroupInitialized(true);
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    void loadUsers();

    return () => {
      cancelled = true;
    };
  }, [groupId, groupInitialized, isAllScope, sideEffectsAllowed]);

  const loadAllScopeUsers = useCallback(async (term: string) => {
    searchRequestId.current += 1;
    const requestId = searchRequestId.current;
    setLoading(true);
    try {
      const results = await CamundaUserService.searchUsers(term);
      if (requestId === searchRequestId.current) {
        setUsers(results);
      }
    } catch (error) {
      console.error('Error searching Camunda users:', error);
      if (requestId === searchRequestId.current) {
        setUsers([]);
      }
    }
    if (requestId === searchRequestId.current) {
      setLoading(false);
    }
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!sideEffectsAllowed || !open || !isAllScope || allScopeActive) return;
      setAllScopeActive(true);
    },
    [allScopeActive, isAllScope, sideEffectsAllowed],
  );

  useEffect(() => {
    if (!sideEffectsAllowed || !isAllScope || !allScopeActive) return;

    const debounceMs = searchTerm.trim() ? SEARCH_DEBOUNCE_MS : 0;
    const handler = setTimeout(() => {
      void loadAllScopeUsers(searchTerm);
    }, debounceMs);

    return () => clearTimeout(handler);
  }, [allScopeActive, isAllScope, loadAllScopeUsers, searchTerm, sideEffectsAllowed]);

  const options = useMemo(() => users.map(mapUserToOption), [users]);

  const initialOption = useMemo(() => {
    if (!fieldValue) return null;
    const match = options.find((option) => option.value === fieldValue);
    if (match) return match;

    const parsedLabelValue = labelValueSchema.safeParse(labelValue);
    if (parsedLabelValue.success) {
      return {
        value: fieldValue,
        label: parsedLabelValue.data,
        displayLabel: parsedLabelValue.data,
      };
    }

    return {
      value: fieldValue,
      label: fieldValue,
      displayLabel: fieldValue,
    };
  }, [fieldValue, labelValue, options]);

  const handleSelect = (option: FormOption | null) => {
    setValue(name, option?.value ?? '', { shouldValidate: true });
    setValue(resolvedLabelFieldName, option?.label ?? '', { shouldValidate: true });
  };

  return (
    <SearchableCombobox
      name={name}
      label={label}
      required={required}
      disabled={!sideEffectsAllowed}
      options={options}
      loading={loading}
      initialOption={initialOption}
      onSelect={handleSelect}
      onOpenChange={handleOpenChange}
      onSearch={isAllScope ? setSearchTerm : undefined}
      searchMode={isAllScope ? 'external' : 'local'}
      placeholder="Selecciona un usuario"
      searchPlaceholder="Buscar por nombre, id o email..."
    />
  );
}
